<template>
  <div class="h-auto bg-white w-full pt-6 pb-8 pl-8 pr-8">
    <!-- Affichage du loader -->
    <div
      v-if="charge"
      class="text-center py-10 text-c20 font-c6 "
      style="display: flex; justify-content: center;"
    >
      <easy-spinner
        type="dots"
        size="70"
      />
    </div>

    <!-- Message vide/aucune information -->
    <div
      v-else-if="displayedData.length === 0"
      class="text-center py-6 text-c18 font-c7"
    >
      {{ showWithoutBackup ? 'Aucun commerce sans sauvegarde trouvé.' : 'Aucun commerce avec sauvegarde trouvé.' }}
    </div>
    
    <!-- Table uniquement si pas en chargement -->
    <div v-if="displayedData.length > 0">
      <div
        v-for="(commerce, i) in displayedData"
        :key="commerce.id || i"
        class="w-full flex text-6b text-c18 font-c7 text-left mb-6 border-gray border-b pb-5"
      >
        <div class="w-1/4">
          {{ commerce.fullName }}
        </div>
        <div class="w-1/5 ml-4">
          {{ commerce.categorie }}
        </div>
        <div class="w-1/5 ml-4">
          {{ commerce.taille }}
        </div>
        <div class="w-1/6 ml-4">
          {{ commerce.lastAction }}
        </div>
        <div class="w-1/6 ml-4">
          {{ formatDate(commerce.relaunchDate) }}
        </div>
        <div class="w-1/6 ml-4">
          <Boutton
            label="Relancer"
            background="#241A72"
            class="w-1/2"
            weight="700"
            :charge="loadingButtons[commerce.id] || false"
            @click="relaunch(commerce.id)"
          />
        </div>
      </div>
    </div>
    
    <!-- Pagination -->
    <div class="flex mt-10 justify-end pb-10 pr-10">
      <div class="w-1/3 flex items-center justify-end">
        <div class="text-c16 font-c6">
          Page {{ currentPage }} sur {{ totalPages }}
        </div>
        <icon
          :data="icons.prev"
          height="50"
          width="50"
          class="icone cursor-pointer ml-4"
          original
          @click="prevPage"
        />
        <icon
          :data="icons.next"
          height="50"
          width="50"
          class="icone cursor-pointer ml-4"
          original
          @click="nextPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { allCommerceTrue, relaunchSms } from "@/component/cdevs/allFunctions";
import Boutton from '../../../component/helper/add/button.vue';
import prev from "../../../assets/icons/prev.svg";
import next from "../../../assets/icons/next.svg";

export default {
  name: 'Cimetiaire',
  
  components: { Boutton },
  
  props: {
    search: {
      type: String,
      default: null
    },
    filtrage: {
      type: String,
      default: null
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    showWithoutBackup: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      charge: false,
      loadingButtons: {},
      allCommerce: [],
      currentPage: 1,
      limit: 20,
      periode: Array.from({ length: 11 }, (_, i) => `${new Date().getFullYear() - i}`),
      icons: { prev, next },
    };
  },
  
  computed: {
    // Étape 1: Filtrer toutes les données selon le critère sauvegarde
    filteredData() {
      if (!this.allCommerce || this.allCommerce.length === 0) {
        return [];
      }
      
      return this.allCommerce.filter(commerce => {
        // Logique de détection de sauvegarde
        const hasBackup = this.checkHasBackup(commerce);
        
        if (this.showWithoutBackup) {
          return !hasBackup; // Ceux SANS sauvegarde
        } else {
          return hasBackup;  // Ceux AVEC sauvegarde
        }
      });
    },
    
    // Étape 2: Calculer le nombre total de pages
    totalPages() {
      return Math.ceil(this.filteredData.length / this.limit) || 1;
    },
    
    // Étape 3: Extraire les données pour la page courante
    displayedData() {
      const start = (this.currentPage - 1) * this.limit;
      const end = start + this.limit;
      return this.filteredData.slice(start, end);
    }
  },
  
  // Watchers
  watch: {
    showWithoutBackup: {
      handler() {
        console.log('🔄 Changement de filtre:', this.showWithoutBackup ? 'AVEC sauvegarde' : 'SANS sauvegarde');
        this.currentPage = 1; // Reset à la page 1
      }
    },
    
    startDate() { this.resetToFirstPage(); },
    endDate() { this.resetToFirstPage(); },
    search() { this.resetToFirstPage(); },
    filtrage() { this.resetToFirstPage(); }
  },
  
  created() {
    this.loadCommerceData();
  },
  
  methods: {
    // Méthode pour déterminer si un commerce a une sauvegarde
    checkHasBackup(commerce) {
      // Version très permissive - ajustez selon vos données
      const lastAction = commerce.lastAction;
      
      // Considéré comme "avec sauvegarde" si lastAction a une vraie valeur
      if (!lastAction) return false;
      if (lastAction === null) return false;
      if (lastAction === undefined) return false;
      if (lastAction === '') return false;
      if (lastAction === 'null') return false;
      if (lastAction === '0') return false;
      if (lastAction === 0) return false;
      if (lastAction === false) return false;
      if (lastAction === 'false') return false;
      
      return true; // Tout le reste est considéré comme "avec sauvegarde"
    },
    
    async loadCommerceData() {
      this.charge = true;
      
      const params = {
        limit: 1000, // Charger TOUT pour faire le filtrage côté front
        page: 1,
        startDate: this.startDate || '2020-01-01',
        endDate: this.endDate || new Date().toISOString().split('T')[0]
      };
      
      if (this.search) {
        params.search = this.search;
      }
      
      if (this.filtrage) {
        params.category = this.filtrage;
      }
      
      console.log(' Paramètres API:', params);
      
      try {
        const response = await allCommerceTrue({ params });
        
        this.allCommerce = response.data || response.rows || [];
        this.charge = false;
        
        console.log('✅ API Response:', {
          total: this.allCommerce.length,
          filtered: this.filteredData.length,
          pages: this.totalPages
        });
        
      } catch (error) {
        console.error('❌ Erreur API:', error);
        this.charge = false;
        this.$toast.error('Erreur lors du chargement');
      }
    },
    
    resetToFirstPage() {
      this.currentPage = 1;
      this.loadCommerceData();
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        console.log('⬅️ Page précédente:', this.currentPage);
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        console.log('➡️ Page suivante:', this.currentPage);
      }
    },
    
    async relaunch(entrepriseId) {
      if (!entrepriseId) {
        console.error("Aucun ID fourni pour la relance.");
        return;
      }
      
      this.loadingButtons[entrepriseId] = true;
      
      try {
        const response = await relaunchSms({ entrepriseId });
        console.log(`Client ${entrepriseId} relancé`, response);
        this.$toast.success("Client relancé avec succès !");
      } catch (error) {
        console.error('Erreur lors de la relance:', error);
        this.$toast.error("Erreur lors de la relance.");
      } finally {
        this.loadingButtons[entrepriseId] = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('fr-FR');
    }
  }
};
</script>